import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import BaseFilter from './BaseFilter';
import { H3 } from '../Typography';
import { FormattedInput } from '../Forms';
import { fromMonetaryAmountString, toMonetaryAmountString } from '../../helpers/formatters/money';
import { getLocaleNumeralDecimal, getLocaleNumeralDelimiter } from '../../helpers/intl';

export type OnChangeFunction = (event: Event) => void;

interface CurrencyRangeFilterProps extends WithTranslation {
    name: string;
    title: string;
    disabled?: boolean;
    values: { min: string; max: string } | undefined;
    onApply: (pendingMin: string, pendingMax: string) => void;
    onDelete: (name: string) => void;
    currency: string;
}

interface FilterSelectState {
    pendingMin: string;
    pendingMax: string;
}

interface AmountInputProps {
    lng: any;
    name: string;
    value: string;
    label: string;
    placeholder: string;
    onChange: (name: string, value: string) => void;
    autoFocus?: boolean;
}

const AmountInput = ({ name, value, lng, label, onChange, placeholder, autoFocus }: AmountInputProps) => {
    const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLTextAreaElement;
        const name = target.name as 'pendingMin' | 'pendingMax';

        onChange(name, target.value);
    };

    return (
        <FormattedInput
            options={{
                numeral: true,
                numericOnly: true,
                numeralThousandsGroupStyle: 'thousand',
                delimiter: getLocaleNumeralDelimiter(lng),
                numeralIntegerScale: 6,
                numeralDecimalMark: getLocaleNumeralDecimal(lng),
                numeralPositiveOnly: true,
                numeralDecimalScale: 2,
            }}
            label={label}
            placeholder={placeholder}
            type="tel"
            inputMode="numeric"
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={() => {}}
            autoComplete="off"
            autoFocus={autoFocus}
        />
    );
};

function unformat(min: string, max: string, lng: string, currency: string) {
    const unformattedMin = min !== '' ? fromMonetaryAmountString(lng, min, currency) : undefined;
    const unformattedMax = max !== '' ? fromMonetaryAmountString(lng, max, currency) : undefined;

    const queryMin = unformattedMin ? unformattedMin.toString() : '';
    const queryMax = unformattedMax ? unformattedMax.toString() : '';
    return { queryMin, queryMax };
}

class CurrencyRangeFilter extends Component<CurrencyRangeFilterProps, FilterSelectState> {
    constructor(props: CurrencyRangeFilterProps, context: any) {
        super(props, context);

        const { values, i18n, currency } = this.props;
        const lng = i18n.language;

        this.state = {
            pendingMin: values && values.min ? toMonetaryAmountString(lng, parseFloat(values.min), currency) : '',
            pendingMax: values && values.max ? toMonetaryAmountString(lng, parseFloat(values.max), currency) : '',
        };
    }

    handleChange = (name: string, value: string) => {
        const nextState = { [name]: value } as Pick<FilterSelectState, keyof FilterSelectState>;
        this.setState(nextState);
    };

    render() {
        const { t, title, name, onApply, onDelete, disabled, values, i18n, currency } = this.props;
        const lng = i18n.language;
        const { pendingMin, pendingMax } = this.state;

        const { queryMin, queryMax } = unformat(pendingMin, pendingMax, lng, currency);
        const activeFilterLabel = this.filter(queryMin, lng, currency, queryMax);

        return (
            <BaseFilter
                initialOpen={!values}
                label={activeFilterLabel}
                disabled={disabled}
                name={name}
                onApply={() => {
                    onApply(queryMin, queryMax);
                }}
                onDelete={() => {
                    onDelete(name);
                }}
            >
                <H3>{title}</H3>
                <AmountInput
                    name="pendingMin"
                    label={t('components.filters.currency_range.minimum')}
                    placeholder={t('components.filters.currency_range.placeholders.minimum')}
                    value={pendingMin}
                    lng={lng}
                    autoFocus={true}
                    onChange={this.handleChange}
                />
                <AmountInput
                    name="pendingMax"
                    label={t('components.filters.currency_range.maximum')}
                    placeholder={t('components.filters.currency_range.placeholders.maximum')}
                    value={pendingMax}
                    lng={lng}
                    onChange={this.handleChange}
                />
            </BaseFilter>
        );
    }

    private filter(queryMin: string, lng: string, currency: string, queryMax: string) {
        const minLabel = queryMin ? toMonetaryAmountString(lng, parseFloat(queryMin), currency) : '';
        const maxLabel = queryMax ? toMonetaryAmountString(lng, parseFloat(queryMax), currency) : '';
        if (minLabel && !maxLabel) {
            return `${minLabel} \u2264`;
        }
        if (maxLabel && !minLabel) {
            return `\u2264 ${maxLabel}`;
        }
        return `${minLabel}-${maxLabel}`;
    }
}

export default withTranslation()<React.ComponentType<CurrencyRangeFilterProps>>(CurrencyRangeFilter);
